/**
 * Conversation management service
 */

import {
  Message,
  ConversationContext,
  PersonalityProfile,
  AIResponse,
  TextGenerationOptions,
  TTSOptions,
  AgentAction,
} from '../core/types';
import { OpenAITextProvider } from '../providers/text/openai';
import { OpenAITTSProvider } from '../providers/tts/openai-tts';

export interface ConversationOptions {
  enableTTS?: boolean;
  enableActions?: boolean;
  maxContextMessages?: number;
  temperature?: number;
}

export class ConversationService {
  private conversations = new Map<string, ConversationContext>();

  constructor(
    private textProvider: OpenAITextProvider,
    private ttsProvider?: OpenAITTSProvider,
    private options: ConversationOptions = {}
  ) {}

  async processMessage(
    userId: string,
    message: string,
    personality?: PersonalityProfile,
    metadata?: Record<string, any>
  ): Promise<AIResponse> {
    // Get or create conversation context
    const conversationId = `${userId}-${Date.now()}`;
    let context = this.conversations.get(conversationId);

    if (!context) {
      context = {
        conversationId,
        userId,
        messages: [],
        metadata: metadata || {},
      };
      this.conversations.set(conversationId, context);
    }

    // Add user message to context
    context.messages.push({
      role: 'user',
      content: message,
      timestamp: new Date(),
    });

    // Prepare messages for generation
    const messages = this.prepareMessages(context, personality);

    // Generate text response
    const textOptions: TextGenerationOptions = {
      temperature: this.options.temperature || 0.7,
      maxTokens: 500,
    };

    const textResponse = await this.textProvider.generateWithMessages(messages, textOptions);

    // Add assistant response to context
    context.messages.push({
      role: 'assistant',
      content: textResponse.text,
      timestamp: new Date(),
    });

    // Trim context if needed
    this.trimContext(context);

    // Generate TTS if enabled
    let audio: ArrayBuffer | undefined;
    if (this.options.enableTTS && this.ttsProvider) {
      const ttsOptions: TTSOptions = {
        voice: metadata?.voice || 'nova',
        speed: 1.0,
      };

      try {
        const audioResponse = await this.ttsProvider.synthesize(textResponse.text, ttsOptions);
        audio = audioResponse.audio;
      } catch (error) {
        console.error('TTS generation failed:', error);
      }
    }

    // Extract actions from response
    const actions = this.options.enableActions ? this.extractActions(textResponse.text) : undefined;

    // Extract emotion
    const emotion = this.detectEmotion(textResponse.text);

    return {
      text: textResponse.text,
      audio,
      emotion,
      actions,
      metadata: {
        conversationId,
        model: textResponse.model,
        usage: textResponse.usage,
      },
    };
  }

  private prepareMessages(
    context: ConversationContext,
    personality?: PersonalityProfile
  ): Message[] {
    const messages: Message[] = [];

    // Add personality as system message
    if (personality) {
      messages.push({
        role: 'system',
        content: this.buildPersonalityPrompt(personality),
      });
    }

    // Add conversation history
    const maxMessages = this.options.maxContextMessages || 10;
    const recentMessages = context.messages.slice(-maxMessages);
    messages.push(...recentMessages);

    return messages;
  }

  private buildPersonalityPrompt(personality: PersonalityProfile): string {
    let prompt = `You are ${personality.name}. ${personality.description}\n\n`;
    prompt += `Your personality traits: ${personality.traits.join(', ')}.\n`;
    prompt += `Communication tone: ${personality.tone}.\n`;

    if (personality.guidelines.length > 0) {
      prompt += `\nGuidelines:\n`;
      personality.guidelines.forEach((guideline) => {
        prompt += `- ${guideline}\n`;
      });
    }

    if (personality.examples && personality.examples.length > 0) {
      prompt += `\nExample interactions:\n`;
      personality.examples.forEach((example) => {
        prompt += `User: ${example.input}\n`;
        prompt += `You: ${example.output}\n\n`;
      });
    }

    return prompt;
  }

  private trimContext(context: ConversationContext): void {
    const maxMessages = (this.options.maxContextMessages || 10) * 2; // Keep some buffer
    if (context.messages.length > maxMessages) {
      // Keep system messages and recent messages
      const systemMessages = context.messages.filter((m) => m.role === 'system');
      const recentMessages = context.messages.slice(-maxMessages);
      context.messages = [...systemMessages, ...recentMessages];
    }
  }

  private extractActions(text: string): AgentAction[] | undefined {
    const actions: AgentAction[] = [];

    // Simple action extraction based on patterns
    // In production, this would be more sophisticated

    // Animation triggers
    if (text.includes('*waves*') || text.includes('*waving*')) {
      actions.push({
        type: 'animation',
        value: 'wave',
        duration: 2000,
      });
    }

    if (text.includes('*dances*') || text.includes('*dancing*')) {
      actions.push({
        type: 'animation',
        value: 'dance',
        duration: 5000,
      });
    }

    // Expression triggers
    if (text.includes(':)') || text.includes('😊')) {
      actions.push({
        type: 'expression',
        value: 'happy',
        duration: 3000,
      });
    }

    return actions.length > 0 ? actions : undefined;
  }

  private detectEmotion(text: string): string | undefined {
    // Simple emotion detection
    const emotions: Record<string, string[]> = {
      happy: ['happy', 'joy', 'excited', 'great', 'wonderful', '😊', ':)'],
      sad: ['sad', 'sorry', 'disappointed', 'unfortunate', '😢', ':('],
      angry: ['angry', 'mad', 'frustrated', 'annoyed', '😠'],
      surprised: ['wow', 'amazing', 'surprised', 'unexpected', '😮'],
      neutral: ['okay', 'alright', 'understand', 'got it'],
    };

    const lowerText = text.toLowerCase();

    for (const [emotion, keywords] of Object.entries(emotions)) {
      if (keywords.some((keyword) => lowerText.includes(keyword))) {
        return emotion;
      }
    }

    return 'neutral';
  }

  clearConversation(conversationId: string): void {
    this.conversations.delete(conversationId);
  }

  getConversation(conversationId: string): ConversationContext | undefined {
    return this.conversations.get(conversationId);
  }
}
